import { useInfiniteQuery, useMutation, useQuery } from "@tanstack/react-query";
import {
  MATRIX_COLLABORATORS_PAGE_SIZE,
  RefreshReactQuery,
} from "../constants/AppConstants";
import { TypeMatrixEnum } from "../constants/PotentialMatrixConstants";
import PotentialMatrixService from "../services/api/potentialMatrix/PotentialMatrix.service";
import { PotentialMatrixFilters } from "./usePotentialMatrixFilters.hook";
import { FilterProps } from "../types/AppTypes";
import { ERROR_LABEL, ERROR_MESSAGE_LABEL } from "../constants/Dictionary";
import { useNotification } from "./notification.hook";
import PotentialMatrixCollaboratorsService from "../services/api/potentialMatrix/PotentialMatrixCollaborators.service";
import { PotentialMatrixFilterKeyEnum } from "../types/api/filters.type";
import { MatrixItem } from "../types/api/potentialMatrix.type";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDebounce } from "use-debounce";
import { useAppSelector } from "./store.hook";

interface usePotentialMatrixProps {
  filters: PotentialMatrixFilters;
  createQueryParams: () => FilterProps[];
  enabled: boolean;
}

export const usePotentialMatrix = ({
  filters,
  createQueryParams,
  enabled,
}: usePotentialMatrixProps) => {
  const { notification } = useNotification();
  const { data, isLoading, isFetching, isError } = useQuery({
    queryKey: ["get-potential-matrix", filters],
    queryFn: ({ signal }) =>
      PotentialMatrixService.get(createQueryParams(), signal),
    staleTime: RefreshReactQuery.MODERATE,
    enabled,
  });

  useEffect(() => {
    if (isError) {
      notification.error(ERROR_LABEL, ERROR_MESSAGE_LABEL);
    }
  }, [isError]);

  return { data, isLoading, isFetching };
};

interface useDownloadPotentialMatrixProps {
  filters: PotentialMatrixFilters;
}

export const usePotentialMatrixDownload = ({
  filters,
}: useDownloadPotentialMatrixProps) => {
  const { notification } = useNotification();

  const { mutateAsync } = useMutation({
    mutationFn: (downloadLimit: number) =>
      PotentialMatrixService.get([
        { key: "year", value: [filters.year] },
        { key: "type", value: [TypeMatrixEnum.FINAL_EVALUATION] },
        { key: "collaboratorsLimit", value: [downloadLimit] },
        { key: "collaboratorsSortBy", value: ["internalGrade"] },
        { key: "collaboratorsSortType", value: ["asc"] },
        { key: "practice", value: [filters.practice[0]] },
        ...(filters.internalGrade.length > 0 &&
        filters.internalGrade[0] !== "all"
          ? [{ key: "internalGrade", value: filters.internalGrade }]
          : []),
      ]),
    onError: (error) => {
      console.error(`${error}`);
      notification.error(ERROR_LABEL, ERROR_MESSAGE_LABEL);
    },
  });

  return { mutateAsync };
};

interface usePotentialMatrixStatsProps {
  filters: PotentialMatrixFilters;
  enabled?: boolean;
}

export const usePotentialMatrixStats = ({
  filters,
  enabled,
}: usePotentialMatrixStatsProps) => {
  const { notification } = useNotification();
  const { config } = useAppSelector((state) => state.config);
  const configExists = config && config.length > 0;

  const { data, isLoading, isFetching, isError } = useQuery({
    queryKey: [
      "get-potential-matrix-stats",
      filters.practice,
      filters.type,
      filters.year,
    ],
    queryFn: ({ signal }) =>
      PotentialMatrixService.getStats(
        [
          { key: "year", value: [filters.year] },
          {
            key: "type",
            value: [filters.type.value],
          },
          ...(filters.practice.length > 0
            ? [
                {
                  key: "practice",
                  value: filters.practice,
                },
              ]
            : []),
        ],
        signal,
      ),
    staleTime: RefreshReactQuery.MODERATE,
    enabled: configExists && enabled,
  });

  useEffect(() => {
    if (isError) {
      notification.error(ERROR_LABEL, ERROR_MESSAGE_LABEL);
    }
  }, [isError]);

  return { data, isLoading, isFetching };
};

interface usePotentialMatrixDetailsProps {
  enabled: boolean;
  searchDebouncedValue: string;
  item: MatrixItem;
  pageNumber: number;
  filters: PotentialMatrixFilters;
}

export const usePotentialMatrixDetails = ({
  enabled,
  searchDebouncedValue,
  item,
  pageNumber,
  filters,
}: usePotentialMatrixDetailsProps) => {
  const { notification } = useNotification();
  const { data, isLoading, isFetching, isError } = useQuery({
    queryKey: [
      "get-matrix-collaborators",
      searchDebouncedValue,
      item,
      pageNumber,
      filters,
    ],
    queryFn: ({ signal }) =>
      PotentialMatrixCollaboratorsService.list(
        MATRIX_COLLABORATORS_PAGE_SIZE,
        pageNumber,
        [
          { key: PotentialMatrixFilterKeyEnum.YEAR, value: [filters.year] },
          {
            key: PotentialMatrixFilterKeyEnum.TYPE,
            value: [filters.type.value],
          },
          ...(filters.practice.length > 0
            ? [
                {
                  key: PotentialMatrixFilterKeyEnum.PRACTICE,
                  value: filters.practice,
                },
              ]
            : []),
          ...(filters.internalGrade.length > 0 &&
          filters.internalGrade[0] !== "all"
            ? [
                {
                  key: PotentialMatrixFilterKeyEnum.INTERNAL_GRADE,
                  value: filters.internalGrade,
                },
              ]
            : []),
          ...(filters.hiddenCollaborators.length > 0
            ? [
                {
                  key: PotentialMatrixFilterKeyEnum.HIDDEN_COLLABORATORS,
                  value: filters.hiddenCollaborators,
                },
              ]
            : []),
          ...[
            { key: "dimension", value: [item!.dimensionId] },
            { key: "screen", value: [item!.screen] },
          ],
          ...(searchDebouncedValue
            ? [{ key: "search", value: [searchDebouncedValue] }]
            : []),
        ],
        signal,
      ),
    staleTime: RefreshReactQuery.MODERATE,
    enabled,
  });

  useEffect(() => {
    if (isError) {
      notification.error(ERROR_LABEL, ERROR_MESSAGE_LABEL);
    }
  }, [isError]);

  return { data, isLoading, isFetching };
};

interface useMatrixCollaboratorsProps {
  filters: PotentialMatrixFilters;
  enabled: boolean;
}

export const useMatrixCollaborators = ({
  filters,
  enabled,
}: useMatrixCollaboratorsProps) => {
  const { notification } = useNotification();
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebounce(search, 500);

  const { data, isLoading, isFetching, hasNextPage, fetchNextPage, isError } =
    useInfiniteQuery({
      queryKey: ["get-matrix-collaborators", filters, debouncedSearch],
      queryFn: ({ pageParam, signal }) =>
        PotentialMatrixCollaboratorsService.list(
          MATRIX_COLLABORATORS_PAGE_SIZE,
          pageParam as number,
          [
            { key: PotentialMatrixFilterKeyEnum.YEAR, value: [filters.year] },
            {
              key: PotentialMatrixFilterKeyEnum.TYPE,
              value: [filters.type.value],
            },
            {
              key: PotentialMatrixFilterKeyEnum.PRACTICE,
              value: filters.practice,
            },
            ...(debouncedSearch
              ? [{ key: "search", value: [debouncedSearch] }]
              : []),
          ],
          signal,
        ),
      initialPageParam: 0,
      getNextPageParam: (lastPage, allPages) =>
        allPages.length * MATRIX_COLLABORATORS_PAGE_SIZE <
        lastPage.pagination.total
          ? allPages.length
          : undefined,
      staleTime: RefreshReactQuery.MODERATE,
      enabled,
    });

  useEffect(() => {
    if (isError) notification.error(ERROR_LABEL, ERROR_MESSAGE_LABEL);
  }, [isError]);

  const items = useMemo(
    () => data?.pages.flatMap((page) => page.data) ?? [],
    [data],
  );

  const fetchMore = useCallback(() => {
    fetchNextPage();
  }, [fetchNextPage]);

  const clearSearch = useCallback(() => setSearch(""), []);

  return {
    data: items,
    initLoading: isLoading,
    canLoadMore: hasNextPage ?? false,
    fetchMore,
    fetchingMore: isFetching && (data?.pages.length ?? 0) > 0,
    search,
    setSearch,
    clearSearch,
  };
};
